// Shared SEO head construction.
//
// Google had ~222 URLs stuck in "Crawled – currently not indexed" because no
// page emitted a <link rel="canonical">, so every tracking-parameter variant of
// a page (?ref= from Ghost, utm_*, fbclid, ...) looked like a separate
// duplicate. Canonicals are built here, once, from the route path only — never
// from the request URL — so no future tracking parameter can reintroduce the
// problem.
//
// Note this deliberately does NOT touch the URLs the site actually links to or
// navigates with: attribution/analytics params keep working exactly as before,
// they are simply absent from the canonical/og:url tags.

// Nuxt inlines `env` values at build time; the fallback keeps the helper
// usable from plain unit tests, where that define never runs.
export const SITE_URL = process.env.baseDomainUrl || 'https://www.pedalsure.com'

// Route prefixes with no search value: authenticated areas, the quote funnel,
// and post-conversion confirmation screens. Matched as full path segments, so
// '/policy' covers '/policy/foo' but never '/policyholder'.
export const NOINDEX_PREFIXES = [
  '/login',
  '/dashboard',
  '/success',
  '/quote-setup',
  '/quote-preview',
  '/quote-complete',
  '/policy',
  '/welcome-back',
  '/auth',
  '/strava-login',
]

// Sections whose *children* are the funnel but whose own landing page is a
// public, indexable front door. '/quote' is the one: '/quote/<id>/make' is a
// step inside somebody's quote, while '/quote' is the page that starts one.
export const NOINDEX_CHILD_PREFIXES = ['/quote']

// Strip the query string and fragment, collapse repeated slashes, and drop a
// trailing slash (except on the root). Anything after '?' or '#' is discarded
// wholesale, which is why no allowlist of parameter names is needed here —
// ref/utm_*/fbclid/gclid/awc/channel/discountPartner/sv1/sv_campaign_id/
// 'cgn:aff'/_$ja and any future addition all fall away for free.
export function getCanonicalPath(path) {
  if (typeof path !== 'string' || !path) {
    return '/'
  }

  const withoutQuery = path.split('?')[0].split('#')[0]
  const absolute = withoutQuery.startsWith('/')
    ? withoutQuery
    : `/${withoutQuery}`
  const collapsed = absolute.replace(/\/{2,}/g, '/')

  return collapsed.length > 1 ? collapsed.replace(/\/+$/, '') : '/'
}

// The clean, absolute, self-referencing URL for a page.
export function getCanonicalUrl(path, baseUrl = SITE_URL) {
  const base = String(baseUrl).replace(/\/+$/, '')
  const canonicalPath = getCanonicalPath(path)

  return canonicalPath === '/' ? `${base}/` : `${base}${canonicalPath}`
}

export function isNoIndexPath(path) {
  const canonicalPath = getCanonicalPath(path)

  const isSection = NOINDEX_PREFIXES.some(
    (prefix) =>
      canonicalPath === prefix || canonicalPath.startsWith(`${prefix}/`)
  )

  return (
    isSection ||
    NOINDEX_CHILD_PREFIXES.some((prefix) =>
      canonicalPath.startsWith(`${prefix}/`)
    )
  )
}

// The head() fragment every layout mixes in. Pages that need a different
// canonical (a blog post keyed off Ghost's own slug, say) override it by
// declaring the same hid — vue-meta resolves the deeper component last.
//
// noindex pages still get a canonical: it is harmless, and if the route is ever
// made indexable the correct tag is already in place.
export function buildSeoHead(path) {
  const meta = []

  if (isNoIndexPath(path)) {
    meta.push({
      hid: 'robots',
      name: 'robots',
      content: 'noindex,follow',
    })
  }

  return {
    meta,
    link: [{ hid: 'canonical', rel: 'canonical', href: getCanonicalUrl(path) }],
  }
}
